<template>
  <div class="confirmation-holder">
    <div class="confirm-button-wrapper">
      <form-button
        v-if="showConfirm"
        variant="tertiary"
        icon="IconConfirmationCheck"
        v-b-tooltip="t('common.operations.confirm.label')"
        classes="confirm-button"
        data-qa="confirm-button"
        :disabled="isInvalid"
        @button-click="emit('confirm')"
      />
    </div>
    <div class="cancel-button-wrapper">
      <form-button
        v-if="showCancel"
        variant="tertiary"
        icon="IconConfirmationCancel"
        v-b-tooltip="t('common.operations.cancel.label')"
        classes="cancel-button"
        data-qa="cancel-button"
        @button-click="emit('cancel')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import FormButton from './FormButton.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface FormConfirmation {
  isInvalid?: boolean;
  showConfirm?: boolean;
  showCancel?: boolean;
}

withDefaults(defineProps<FormConfirmation>(), {
  showConfirm: true,
  showCancel: true,
});

interface FormConfirmationEmits {
  (e: 'confirm'): void;

  (e: 'cancel'): void;
}

const emit = defineEmits<FormConfirmationEmits>();
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

$button-size: 22px;

.confirmation-holder {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: center;
}

.confirm-button-wrapper,
.cancel-button-wrapper {
  height: $button-size;
  width: $button-size;
}

:deep(.confirm-button-wrapper .confirm-button),
:deep(.cancel-button-wrapper .cancel-button) {
  height: $button-size;
  padding: 0;
  width: $button-size;

  &:hover {
    .svg-rectangle {
      stroke: map.get(colors.$colors, 'azure-radiance');
    }
  }

  &:active {
    .svg-rectangle {
      fill: map.get(colors.$colors, 'spindle');
      stroke: map.get(colors.$colors, 'biscay');
    }

    .svg-check,
    .svg-cancel {
      fill: map.get(colors.$colors, 'biscay');
    }
  }

  &:focus-visible {
    outline: none;
  }
}
</style>
